import type { AxiosError, AxiosRequestConfig } from 'axios';

import { refreshAccessToken, signOut } from '@/auth/AuthUtils';
import { getUserLocale } from '@/utils/getUserLocale';
import { getDeviceTimeZone } from '@/utils/timeZone';
import Axios from 'axios';
import { getBaseUrl } from '@/api/getBaseUrl';
import qs from 'qs';
import { TokenRefreshError } from '@/auth/Errors';
import { getNativeAppInfo } from '@/appUpdate/nativeAppInfo';

const BASE_URL = getBaseUrl();

export const AXIOS_INSTANCE = Axios.create({
	baseURL: BASE_URL,
	paramsSerializer: params => {
		return qs.stringify(params, { arrayFormat: 'repeat' });
	},
});

AXIOS_INSTANCE.interceptors.request.use(async config => {
	config.headers['Accept-Language'] = getUserLocale();

	// The API stores this zone on sign-in, sign-up and token refresh.
	const timeZone = getDeviceTimeZone();
	if (timeZone) {
		config.headers['X-Timezone'] = timeZone;
	}

	// The API turns away builds below the minimum version with 426; see UpdateGate.
	const appInfo = await getNativeAppInfo();
	if (appInfo) {
		config.headers['X-App-Platform'] = appInfo.platform;
		config.headers['X-App-Version'] = appInfo.version;
	}

	// Skip token refresh for auth endpoint to prevent infinite loops. `/auth/logout` carries the
	// refresh token itself, and `/auth/biometric/login` runs with no session at all.
	// `/app/version-policy` is public and runs on every launch and resume, signed in or not; a
	// failed refresh would sign out and clear the query cache each time.
	if (
		config.url?.startsWith('/app/version-policy')
		|| config.url?.startsWith('/auth/refresh')
		|| config.url?.startsWith('/auth/logout')
		|| config.url?.startsWith('/auth/login')
		|| config.url?.startsWith('/auth/biometric/login')
		|| config.url?.startsWith('/auth/register')
	) {
		return config;
	}

	try {
		const session = await refreshAccessToken();
		// refreshAccessToken now guarantees a valid session or throws an error
		config.headers.Authorization = `Bearer ${session.accessToken}`;
	} catch (error) {
		if (error instanceof TokenRefreshError) {
			// For critical auth errors that should trigger sign-out,
			// we still continue the request to let the response interceptor handle the 401
			if (error.shouldSignOut) {
				// Clear current session on refresh failure
				signOut();
				// Don't add Authorization header - let the endpoint return 401
			} else {
				// For recoverable errors (network, server), log but continue
				console.warn('Recoverable error in request interceptor:', error.message);
			}
		}

		// Always continue with the request - let response interceptor handle auth failures
	}

	return config;
});

export const customInstance = <T>(config: AxiosRequestConfig, options?: AxiosRequestConfig): Promise<T> => {
	const controller = new AbortController();

	const promise = AXIOS_INSTANCE({
		...config,
		...options,
		signal: controller.signal,
	}).then(({ data }) => data);

	// @ts-expect-error this is expected to happen
	promise.cancel = () => {
		controller.abort('Query was cancelled');
	};

	return promise;
};

export type ErrorType<Error = unknown> = AxiosError<Error>;

export type BodyType<BodyData> = BodyData;
